import { useState } from "react";

/** The browser's copy of one table's columns. A convenience: without it the defaults show. */
function storageKey(table: string): string {
  return `countdown.columns.${table}`;
}

/**
 * What this browser remembered for a table, limited to the columns it still has and to
 * true/false. Anything else, an unreadable entry or storage that throws (a private window),
 * reads as nothing remembered.
 */
export function readColumns<K extends string>(
  table: string,
  defaults: Readonly<Record<K, boolean>>,
): Partial<Record<K, boolean>> {
  try {
    const raw = localStorage.getItem(storageKey(table));
    const stored: unknown = raw === null ? null : JSON.parse(raw);
    if (typeof stored !== "object" || stored === null) {
      return {};
    }
    const entries = Object.entries(stored).filter(
      ([column, shown]) => column in defaults && typeof shown === "boolean",
    );
    return Object.fromEntries(entries) as Partial<Record<K, boolean>>;
  } catch (error: unknown) {
    console.error("[columns] stored choice unreadable", error);
    return {};
  }
}

/** Which optional columns of a table are shown, remembered per browser. */
export function useStoredColumns<K extends string>(
  table: string,
  defaults: Readonly<Record<K, boolean>>,
): readonly [Readonly<Record<K, boolean>>, (column: K, shown: boolean) => void] {
  const [columns, setColumns] = useState<Readonly<Record<K, boolean>>>(() => ({
    ...defaults,
    ...readColumns(table, defaults),
  }));
  const show = (column: K, shown: boolean): void => {
    setColumns((current) => {
      const next = { ...current, [column]: shown };
      try {
        localStorage.setItem(storageKey(table), JSON.stringify(next));
      } catch (error: unknown) {
        console.error("[columns] choice not stored", error);
      }
      return next;
    });
  };
  return [columns, show] as const;
}
